/**
 * Trade-offer pedal snapshot read/write utilities.
 *
 * KNOWN LIMITATION — doc pedalSnapshots vs counter offers
 * -------------------------------------------------------
 * Doc-level `pedalSnapshots` is written at offer create (and on accept/expire)
 * but is NOT updated when a counter is submitted. It therefore reflects
 * create-time terms only.
 *
 * For countered offers where price or condition changed in the counter round,
 * `resolvePedalDisplayFromTrade` will show create-time values for any field
 * already present in the doc snapshot (doc is merged first; first-write-wins).
 *
 * Counter-added or counter-swapped pedals resolve correctly via history, since
 * those pedal IDs are absent from the create-time doc snapshot.
 *
 * Follow-up: refresh doc `pedalSnapshots` on counter submission so doc
 * snapshot and final offer terms stay aligned.
 */
import { getInitiatorPedalIds } from './tradeUtils';

export const buildListingSnapshot = (listing) => {
  if (!listing) return null;
  const id = listing.id;
  if (!id) return null;
  return {
    id,
    make: listing.make || '',
    model: listing.model || '',
    condition: listing.condition || null,
    price: Number(listing.price || 0),
    images: listing.images || { top: listing.images?.top || listing.imageUrl || null },
    imageUrl: listing.images?.top || listing.imageUrl || null,
    // Photo-less listing: its stand-in (plan §4), so offer cards never go blank.
    placeholderImage: listing.placeholderImage || null,
  };
};

export const buildPedalSnapshotsPayload = (initiatorListings, responderListing) => {
  const initiatorPedals = (initiatorListings || [])
    .map(buildListingSnapshot)
    .filter(Boolean);
  if (!initiatorPedals.length || !responderListing) return null;
  return {
    initiatorPedals,
    initiatorPedal: initiatorPedals[0],
    responderPedal: buildListingSnapshot(responderListing),
    snapshotCreatedAt: new Date().toISOString(),
  };
};

const getDocSnapshotList = (trade) => {
  if (!trade?.pedalSnapshots) return [];
  const snaps = trade.pedalSnapshots;
  if (snaps.initiatorPedals?.length) return snaps.initiatorPedals;
  if (snaps.initiatorPedal) return [snaps.initiatorPedal];
  return [];
};

export const getPedalFromDocSnapshots = (trade, pedalId) => {
  if (!trade || !pedalId) return null;
  const initiatorMatch = getDocSnapshotList(trade).find((p) => p.id === pedalId);
  if (initiatorMatch) return initiatorMatch;
  if (trade.pedalSnapshots?.responderPedal?.id === pedalId) {
    return trade.pedalSnapshots.responderPedal;
  }
  return null;
};

const isResponderPedal = (trade, pedalId) => trade?.responderPedalId === pedalId;

const mergeDetailFields = (base, incoming) => {
  if (!incoming) return base;
  const merged = { ...(base || {}) };
  for (const [key, value] of Object.entries(incoming)) {
    if (value !== undefined && value !== null && value !== '') {
      const existing = merged[key];
      if (existing === undefined || existing === null || existing === '') {
        merged[key] = value;
      }
    }
  }
  return merged;
};

export const getMergedHistoryPedalDetail = (trade, pedalId) => {
  if (!trade || !pedalId) return null;
  const asInitiator = getInitiatorPedalIds(trade).includes(pedalId);
  const asResponder = isResponderPedal(trade, pedalId);
  let merged = null;

  for (const entry of trade.history || []) {
    const snap = entry.offerSnapshot;
    if (!snap) continue;

    if (asInitiator) {
      merged = mergeDetailFields(merged, snap.initiatorPedalDetails?.[pedalId]);
      if (snap.initiatorPedalPrices?.[pedalId] != null) {
        merged = mergeDetailFields(merged, { price: Number(snap.initiatorPedalPrices[pedalId]) });
      }
    }

    if (asResponder && snap.responderPedalDetail) {
      merged = mergeDetailFields(merged, snap.responderPedalDetail);
      if (snap.responderPedalPrice != null) {
        merged = mergeDetailFields(merged, { price: Number(snap.responderPedalPrice) });
      }
    }
  }

  return merged;
};

export const pedalDisplayFromSnapshotRecord = (record, pedalId, fallbackPrice) => {
  if (!record) return null;
  const make = record.make || '';
  const model = record.model || '';
  let resolvedMake = make;
  let resolvedModel = model;
  if (!resolvedMake && !resolvedModel && record.name) {
    const parts = record.name.trim().split(/\s+/);
    resolvedMake = parts[0] || '';
    resolvedModel = parts.slice(1).join(' ') || '';
  }
  return {
    id: record.id || pedalId,
    make: resolvedMake,
    model: resolvedModel,
    images: record.images || { top: record.imageUrl || record.images?.top || null },
    imageUrl: record.imageUrl || record.images?.top || null,
    // History details store the stand-in as a flat placeholderUrl; lift it so
    // placeholderFor() can use it like a doc snapshot's placeholderImage.
    placeholderImage: record.placeholderImage
      || (record.placeholderUrl ? { source: /reverb/i.test(record.placeholderUrl) ? 'reverb' : 'sketch', url: record.placeholderUrl, link: null } : null),
    condition: record.condition || null,
    price: record.price != null ? Number(record.price) : (fallbackPrice != null ? Number(fallbackPrice) : 0),
  };
};

export const resolvePedalDisplayFromTrade = (trade, pedalId, livePedal = null) => {
  if (!trade || !pedalId) return null;

  const docSnapshot = getPedalFromDocSnapshots(trade, pedalId);
  const historyDetail = getMergedHistoryPedalDetail(trade, pedalId);
  const sources = [docSnapshot, historyDetail, livePedal].filter(Boolean);

  if (sources.length === 0) return null;

  let merged = {};
  for (const source of sources) {
    merged = mergeDetailFields(merged, source);
  }

  const display = pedalDisplayFromSnapshotRecord(merged, pedalId, merged.price);
  if (!display) return null;

  // Photos added AFTER the snapshot was frozen (the offer-flow photo gate) must
  // show: a snapshot taken from a photo-less listing has an empty images map,
  // which the first-non-empty merge above would otherwise keep forever.
  const liveTop = livePedal?.images?.top || livePedal?.imageUrl || null;
  const frozenTop = display.images?.top || display.imageUrl || null;
  if (liveTop && !frozenTop) {
    display.images = livePedal.images || { top: liveTop };
    display.imageUrl = liveTop;
  }

  if ((!display.condition || display.condition === '—') && livePedal?.condition) {
    display.condition = livePedal.condition;
  }

  return display;
};
